import React, { useCallback, useEffect, useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import { FaXmark } from "react-icons/fa6";
import { useGetUsersQuery, useSearchUserQuery } from "./adminApiSlice";
import useAuth from "../../hooks/useAuth";
import useTitle from "../../hooks/useTitle";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import { IoMdSearch } from "react-icons/io";
import LoadingIndicator from "../../components/BasicComponents/LoadingIndicator";

export default function Admin() {
  useTitle("Vizta Admin");
  const { roles } = useAuth();
  const navigate = useNavigate();
  // // console.log(roles);
  const [searchQuery, setSearchQuery] = useState("");
  const [usersData, setUserData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  // Use debounce to delay the search query update
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 300),
    []
  );
  const {
    data: searchedUsersData,
    isLoading: isSearchedUsersLoading,
    isError: isSearchedUsersError,
    isSuccess: isSearchedUsersSuccess,
    error: searchedUserError,
  } = useSearchUserQuery(searchQuery, {
    skip: searchQuery.length === 0, // Skip fetching if search query is empty
  });
  // Fetch users data
  const {
    data: users,
    isLoading,
    isError,
    error,
    isSuccess,
  } = useGetUsersQuery();

  // console.log(users);

  // // console.log(searchedUsersData);
  if (isSearchedUsersLoading) {
    <LoadingIndicator />;
  }
  if (isSearchedUsersError) {
    // console.log(searchedUserError["data"]["message"]);
  }
  if (isSearchedUsersSuccess) {
    // console.log(searchedUsersData);
  }
  useEffect(() => {
    if (searchQuery === "" && users) {
      // No search query; set userData to all users
      const userDetailsArray = users.map((user) => ({
        userId: user._id,
        username: user.username,
        email: user.email,
        roles: user.roles,
        department: user.department,
        lastLoginDate: user.lastLoginDate,
        status: user.status,
        Active: true,
      }));
      setUserData(userDetailsArray);
    } else if (searchQuery !== "" && searchedUsersData) {
      // Valid search query and results; set userData to searched users
      const searchedUserDetailsArray = searchedUsersData.map((user) => ({
        userId: user._id,
        username: user.username,
        email: user.email,
        roles: user.roles,
        department: user.department,
        lastLoginDate: user.lastLoginDate,
        status: user.status,
        Active: true,
      }));
      setUserData(searchedUserDetailsArray);
    }
  }, [users, searchedUsersData, searchQuery]);

  const handleAddUserButton = () => {
    navigate("/home/dash/addNewUser");
  };

  const handleSingleClick = () => {
    if (selectedRow !== null) {
      // console.log(usersData[selectedRow]);
      const { userId } = usersData[selectedRow];
      navigate(`/home/dash/editUser?user=${userId}`);
    }
  };

  const handleDoubleClick = (selectedRow) => {
    if (selectedRow !== null) {
      // console.log(usersData[selectedRow]);
      const { userId } = usersData[selectedRow];
      navigate(`/home/dash/editUser?user=${userId}`);
    }
  };

  const handleInputChange = (event) => {
    const query = event.target.value;
    // console.log(query);
    debouncedSearch(query);
  };
  // console.log(usersData);

  return (
    <main className="overflow-hidden flex flex-col w-full h-[94vh] min-h-[94vh]">
      <section className="flex items-center justify-between px-[8px] py-[6px] border-y border-gray-400 h-auto">
        <div className="w-2/3 flex items-center justify-start gap-2 font-medium">
          Users List
          <button
            className={`flex justify-start items-center rounded border border-gray-300 text-gray-800 shadow gap-1 px-1 py-[1px] h-full w-auto`}
          >
            <IoMdSearch className="text-gray-400" />
            <input
              className={`w-[16rem] placeholder:text-[12px] pb-[4px] focus:outline-none text-sm`}
              type="text"
              placeholder="search users..."
              onChange={handleInputChange}
            />
          </button>
          {isError ? (
            <span className="text-red-500 w-auto">{error}</span>
          ) : null}
          {isSearchedUsersError ? (
            <span className="text-red-500 w-auto">
              {searchedUserError["data"]["message"]}
            </span>
          ) : null}
        </div>
        <div className="flex items-center justify-center gap-4">
          <button
            className={`border border-gray-400 rounded px-3 text-[12px] py-1 truncate ${
              selectedRow === null
                ? "bg-gray-100 shadow-none"
                : "bg-pink-600 text-white shadow-lg"
            }`}
            disabled={selectedRow === null}
            onClick={handleSingleClick}
          >
            Edit User
          </button>
          <button
            className="border border-gray-400 rounded px-5 text-[12px] py-1 bg-blue-950 text-white truncate"
            onClick={handleAddUserButton}
          >
            Add User
          </button>
        </div>
      </section>
      <section className="flex flex-col gap-1 h-[94%] border-gray-300 border pt-2">
        {isLoading && <LoadingIndicator />}
        {isSuccess && (
          <div className="overflow-y-auto h-full">
            <table className="border-none w-full">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="border-b text-left px-2">User Name</th>
                  <th className="border-b text-left px-2">Email</th>
                  <th className="border-b text-left px-2">Department</th>
                  <th className="border-b text-left px-2">Role</th>
                  <th className="border-b text-left px-2">Last Login</th>
                  <th className="border-b text-left px-2">Active</th>
                </tr>
              </thead>
              <tbody>
                {usersData !== undefined &&
                  usersData.length &&
                  usersData.map((row, index) => (
                    <tr
                      key={index}
                      className={`${
                        selectedRow === index
                          ? "bg-blue-950 text-white"
                          : index % 2 === 0
                          ? "bg-gray-100"
                          : ""
                      } cursor-pointer`}
                      onClick={() => setSelectedRow(index)}
                      onDoubleClick={() => handleDoubleClick(index)}
                    >
                      <td className="border-b text-left px-2 my-2">
                        {row.username}
                      </td>
                      <td className="border-b text-left px-2 my-2">
                        {row.email}
                      </td>
                      <td className="border-b text-left px-2 my-2">
                        {row.department}
                      </td>
                      <td className="border-b text-left px-2 my-2 truncate">
                        {row.roles &&
                          Object.entries(row.roles).map(([role, value]) => (
                            <div key={role}>{value}</div>
                          ))}
                      </td>
                      <td className="border-b text-left px-2 my-2">
                        {row.lastLoginDate
                          ? format(
                              new Date(row.lastLoginDate),
                              "MMMM dd, yyyy h:mm a"
                            )
                          : "Not login"}
                      </td>
                      <td className="border-b text-left px-2 my-2">
                        {row.Active === true ? (
                          <GiCheckMark
                            className={`${
                              selectedRow === index
                                ? " text-white"
                                : "text-green-700"
                            } `}
                          />
                        ) : (
                          <FaXmark
                            className={`${
                              selectedRow === index
                                ? "text-white"
                                : "text-red-700 "
                            } `}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
